import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { getContactUs, saveContactUs } from "../../services/api/getAll";

const languages = ["en", "ar", "es"];

const ContactUsAdmin = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactData, setContactData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getContactUs();
        const dataByLang: Record<string, any> = {};
        for (const lang of languages) {
          const found = res.items.find((item: any) => item.languageCode === lang);
          dataByLang[lang] = found || {
            id: 0,
            address: "",
            phone: "",
            email: "",
            mapEmbedUrl: "",
            languageCode: lang,
          };
        }
        setContactData(dataByLang);
      } catch (err) {
        console.error("Error fetching contact info", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (lang: string, field: string, value: string) => {
    setContactData((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      },
    }));
  };

  const handleSave = async (lang: string) => {
    const data = contactData[lang];
    const isIframe = data.mapEmbedUrl?.includes("<iframe");
    if (!isIframe) {
      setErrors((prev) => ({
        ...prev,
        [lang]: "Map Embed URL must contain a valid <iframe> tag.",
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        [lang]: "",
      }));
    }

    try {
      setSaving(true);
      await saveContactUs(data);
      alert(`Saved ${lang.toUpperCase()} contact info successfully!`);
    } catch (err) {
      console.error("Save failed", err);
      alert("Failed to save contact info.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress sx={{ color: "#FFAE00" }} />;

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: "auto",
        mt: 5,
        px: 2,
        fontFamily: "Poppins",
        bgcolor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 0 10px rgba(0,0,0,0.06)",
        pb: 4,
      }}
    >
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        centered
        TabIndicatorProps={{ style: { backgroundColor: "#FFAE00" } }}
      >
        {languages.map((lang) => (
          <Tab
            key={lang}
            label={lang.toUpperCase()}
            sx={{
              fontWeight: 600,
              fontFamily: "Poppins",
              color: tab === languages.indexOf(lang) ? "#FFAE00" : "#777",
            }}
          />
        ))}
      </Tabs>

      {languages.map((lang, idx) => {
        const data = contactData[lang];
        return (
          tab === idx && (
            <Box
              key={lang}
              mt={4}
              component="form"
              noValidate
              autoComplete="off"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {["phone", "email", "address"].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field.charAt(0).toUpperCase() + field.slice(1)}
                  value={data[field]}
                  onChange={(e) => handleChange(lang, field, e.target.value)}
                  placeholder={`Enter ${field}`}
                  InputLabelProps={{
                    sx: { color: "#333", fontWeight: 600 },
                  }}
                  InputProps={{
                    sx: {
                      fontFamily: "Poppins",
                      borderRadius: "12px",
                      backgroundColor: "#F4FEFF",
                      color: "#000",
                      "&::placeholder": { color: "#888" },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#C4C4C4",
                      },
                      "&:hover fieldset": {
                        borderColor: "#FFAE00",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#FFAE00",
                      },
                    },
                  }}
                />
              ))}

              <TextField
                key="mapEmbedUrl"
                fullWidth
                label="Map Embed URL"
                value={data.mapEmbedUrl}
                onChange={(e) => handleChange(lang, "mapEmbedUrl", e.target.value)}
                placeholder="Paste the iframe embed code here"
                error={Boolean(errors[lang])}
                helperText={errors[lang]}
                InputLabelProps={{
                  sx: { color: "#333", fontWeight: 600 },
                }}
                InputProps={{
                  sx: {
                    fontFamily: "Poppins",
                    borderRadius: "12px",
                    backgroundColor: "#F4FEFF",
                    color: "#000",
                    "&::placeholder": { color: "#888" },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#C4C4C4",
                    },
                    "&:hover fieldset": {
                      borderColor: "#FFAE00",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFAE00",
                    },
                  },
                }}
              />

              <Box textAlign="center">
                <Button
                  variant="contained"
                  onClick={() => handleSave(lang)}
                  disabled={saving}
                  sx={{
                    px: 5,
                    py: 1.5,
                    bgcolor: "#FFAE00",
                    color: "#000",
                    fontWeight: 700,
                    borderRadius: "10px",
                    fontFamily: "Poppins",
                    "&:hover": {
                      bgcolor: "#e29a00",
                    },
                  }}
                >
                  {saving ? "Saving..." : `Save ${lang.toUpperCase()}`}
                </Button>
              </Box>
            </Box>
          )
        );
      })}
    </Box>
  );
};

export default ContactUsAdmin;
